import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import {
  Banner,
  Button,
  Cluster,
  Fab,
  Icon,
  Icons,
  IconButton,
  Menu,
  MenuItem,
  Sheet,
  Spinner,
  Stack,
  StatusBadge,
  Surface,
  Text,
  TextArea,
} from '../ui';
import { useAuth } from '../lib/auth';
import { copyToClipboard } from '../lib/clipboard';
import { useChat } from '../lib/useChat';
import type { RiskLevel } from '@app/shared';
import {
  isThinking,
  transcriptToMarkdown,
  type TranscriptItem,
  type TurnStatus,
} from '../lib/chatTranscript';
import { downloadTextFile } from '../lib/reportExport';
import { printChatMessageAsPdf, printChatSessionAsPdf } from '../lib/chatPdf';
import { Markdown } from './Markdown';

/**
 * The always-present quick-chat assistant (§21, §14, WP-C4). A floating launcher on
 * every authed route opens a right-hand Sheet that streams the assistant's answer —
 * text bubbles that grow with `model_delta`, inline tool-call chips, and inline
 * Approve/Reject cards for action turns. Conversation history loads on open; the
 * input is disabled while a turn streams; failed/limit turns show a terminal note.
 *
 * Rebuilt on the `@/ui` design layer (Chakra engine) — semantic tokens only, no raw
 * CSS beyond token-referencing inline styles. Test ids are stable for the WP-C5 e2e:
 * chat-launcher, chat-panel, chat-input, chat-send, chat-message (+ data-role /
 * data-status), chat-tool-chip (+ data-status), chat-approval, chat-approve,
 * chat-reject, chat-status (+ data-state).
 */

type BadgeTone = 'neutral' | 'warning' | 'danger';
const RISK_TONE: Record<RiskLevel, BadgeTone> = {
  low: 'neutral',
  medium: 'warning',
  high: 'danger',
};

const TERMINAL_TEXT: Record<Exclude<TurnStatus, 'completed'>, string> = {
  failed: 'The assistant hit an error and stopped.',
  limit_reached: 'The assistant reached this turn’s budget and stopped.',
};

/**
 * A "copy to clipboard" affordance shown next to a chat error. Copies the raw error
 * text so it can be pasted into a bug report / search, with a brief ✓ confirmation.
 * Applies to any agent's chat — tool failures and turn-level (terminal) failures alike.
 */
function CopyErrorButton({ error, testid }: { error: string; testid: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onCopy = async () => {
    if (await copyToClipboard(error)) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  };
  return (
    <IconButton
      variant="ghost"
      size="sm"
      aria-label="Copy error to clipboard"
      title={copied ? 'Copied' : 'Copy error'}
      data-testid={testid}
      onClick={onCopy}
      icon={<Icon as={copied ? Icons.Check : Icons.Copy} size="sm" tone="current" />}
    />
  );
}

function ToolChip({ item }: { item: Extract<TranscriptItem, { kind: 'tool_call' }> }) {
  const errorText = item.status === 'error' && item.error ? item.error : '';
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const label =
    item.status === 'running'
      ? `Calling ${item.tool}…`
      : item.status === 'error'
        ? `${item.tool} failed`
        : `${item.tool}`;
  const tone: 'neutral' | 'success' | 'danger' =
    item.status === 'error' ? 'danger' : item.status === 'ok' ? 'success' : 'neutral';
  const glyph =
    item.status === 'running'
      ? Icons.Loader
      : item.status === 'error'
        ? Icons.CircleAlert
        : Icons.CircleCheck;

  // A failed tool chip surfaces its error via a native tooltip; clicking (or Enter/Space)
  // copies it to the clipboard, with a brief confirmation in the tooltip.
  const handleCopy = async () => {
    if (!errorText) return;
    if (await copyToClipboard(errorText)) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  };
  const clickable = Boolean(errorText);
  const title = errorText
    ? copied
      ? 'Copied error to clipboard'
      : `${errorText}\n(click to copy)`
    : undefined;

  return (
    <StatusBadge
      tone={tone}
      size="sm"
      icon={glyph}
      label={label}
      data-testid="chat-tool-chip"
      data-status={item.status}
      title={title}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `${item.tool} failed — click to copy error` : undefined}
      onClick={clickable ? handleCopy : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void handleCopy();
              }
            }
          : undefined
      }
      style={{ maxWidth: '100%', cursor: clickable ? 'pointer' : 'default' }}
    />
  );
}

function ApprovalCard({
  item,
  onDecide,
}: {
  item: Extract<TranscriptItem, { kind: 'approval' }>;
  onDecide: (approvalId: string, decision: 'approve' | 'reject') => void;
}) {
  const decided = item.decision != null;
  return (
    <Surface
      bordered
      padding="4"
      radius="lg"
      data-testid="chat-approval"
      style={{ borderColor: 'var(--ehe-colors-status-warning)' }}
    >
      <Stack gap="3">
        <Cluster gap="2" align="center">
          <Text size="sm" weight="semibold">
            Approval needed
          </Text>
          <StatusBadge tone="neutral" size="sm" label={item.tool} />
          <StatusBadge tone={RISK_TONE[item.risk]} size="sm" label={item.risk} />
        </Cluster>
        <Text size="sm" tone="secondary">
          {item.reason}
        </Text>
        {/* Real design-system Buttons: native <button>, keyboard focus (`:focus-visible`),
            disabled semantics and theming from the system. Behaviour + testids unchanged. */}
        <Cluster gap="2">
          <Button
            size="sm"
            variant="primary"
            data-testid="chat-approve"
            disabled={decided}
            onClick={() => onDecide(item.approvalId, 'approve')}
          >
            {item.decision === 'approve' ? 'Approved' : 'Approve'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            data-testid="chat-reject"
            disabled={decided}
            onClick={() => onDecide(item.approvalId, 'reject')}
          >
            {item.decision === 'reject' ? 'Rejected' : 'Reject'}
          </Button>
        </Cluster>
      </Stack>
    </Surface>
  );
}

/**
 * A collapsible "Thinking" section (§21, v1.11) — the model's extended-reasoning summary
 * for this turn segment, folded in from `thinking` events. Collapsed by default, like
 * Claude's "Thought for a moment ▸". Renders above the assistant bubble it precedes.
 */
function ReasoningSection({ reasoning }: { reasoning: string }) {
  const [open, setOpen] = useState(false);
  const toggleStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 4px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--ehe-colors-text-secondary)',
    font: 'inherit',
    borderRadius: 'var(--ehe-radii-sm)',
  };
  return (
    <div data-testid="chat-reasoning" style={{ marginBottom: '4px' }}>
      <button
        type="button"
        data-testid="chat-reasoning-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={toggleStyle}
      >
        <Icon as={Icons.Brain} size="xs" tone="current" />
        <Text as="span" size="xs" tone="secondary">
          Thinking
        </Text>
        <Icon as={open ? Icons.ChevronDown : Icons.ChevronRight} size="xs" tone="current" />
      </button>
      {open && (
        <div
          style={{
            marginTop: '4px',
            padding: '6px 10px',
            borderLeft: '2px solid var(--ehe-colors-border-default)',
          }}
        >
          <Text size="xs" tone="secondary" style={{ whiteSpace: 'pre-wrap' }}>
            {reasoning}
          </Text>
        </div>
      )}
    </div>
  );
}

/**
 * The live "Thinking…" indicator (§21, v1.11) — a subtle activity row shown while a turn
 * is in flight and the model has gone quiet (see `isThinking`), the way Claude shows
 * activity before output.
 */
function ThinkingIndicator() {
  return (
    <div
      data-testid="chat-thinking"
      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px' }}
    >
      <Spinner size="xs" tone="muted" label="Thinking" />
      <Text size="xs" tone="secondary">
        Thinking…
      </Text>
    </div>
  );
}

function MessageBubble({
  item,
}: {
  item: Extract<TranscriptItem, { kind: 'user' | 'assistant' }>;
}) {
  const isUser = item.kind === 'user';
  const terminal = item.kind === 'assistant' && item.status && item.status !== 'completed';
  const reasoning = item.kind === 'assistant' ? item.reasoning : undefined;
  const hasBubble = isUser || item.content.length > 0 || Boolean(terminal);
  // Offer the per-message PDF only for an assistant turn that carries something to
  // export (prose and/or reasoning) — an empty terminal-only bubble has nothing to save.
  const canDownload =
    item.kind === 'assistant' && (item.content.trim().length > 0 || Boolean(item.reasoning?.trim()));
  const userBubbleStyle: CSSProperties = {
    background: 'var(--ehe-colors-action-primary)',
    color: 'var(--ehe-colors-text-inverse)',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  };
  return (
    <div
      data-testid="chat-message"
      data-role={item.kind}
      data-status={item.kind === 'assistant' ? (item.status ?? '') : ''}
      style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
    >
      <div style={{ maxWidth: '85%', minWidth: 0 }}>
        {reasoning ? <ReasoningSection reasoning={reasoning} /> : null}
        {hasBubble &&
          (isUser ? (
            <Surface padding="3" radius="lg" style={userBubbleStyle}>
              <Text as="span" size="sm" tone="inverse" style={{ whiteSpace: 'pre-wrap' }}>
                {item.content}
              </Text>
            </Surface>
          ) : item.content ? (
            <Surface
              tone="raised"
              bordered
              padding="3"
              radius="lg"
              style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            >
              <Markdown text={item.content} />
            </Surface>
          ) : null)}
        {terminal &&
          (() => {
            const errorText =
              item.error ?? TERMINAL_TEXT[item.status as Exclude<TurnStatus, 'completed'>];
            const isLimit = item.status === 'limit_reached';
            return (
              <div style={{ marginTop: '6px' }}>
                <Banner
                  tone={isLimit ? 'warning' : 'danger'}
                  role={isLimit ? 'status' : 'alert'}
                  icon={isLimit ? Icons.CircleAlert : Icons.CircleX}
                  action={<CopyErrorButton error={errorText} testid="chat-error-copy" />}
                >
                  {errorText}
                </Banner>
              </div>
            );
          })()}
        {canDownload && item.kind === 'assistant' && (
          <div style={{ marginTop: '2px', display: 'flex' }}>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Download this message as PDF"
              title="Download this message as PDF"
              data-testid="chat-message-download-pdf"
              onClick={() => printChatMessageAsPdf(item)}
              icon={<Icon as={Icons.FileDown} size="sm" tone="secondary" />}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Custom DOM event other shell surfaces (e.g. the ⌘K command menu) fire to open the
 *  quick-chat assistant without a shared store. */
export const OPEN_CHAT_EVENT = 'ehe:open-chat';

export function ChatPanel() {
  const { workspaceId } = useAuth();
  const [open, setOpen] = useState(false);

  // Allow the command menu / other shell actions to open the assistant.
  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener(OPEN_CHAT_EVENT, openChat);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, openChat);
  }, []);

  // The launcher is only meaningful once a workspace is resolved.
  if (!workspaceId) return null;
  return <ChatPanelInner workspaceId={workspaceId} open={open} setOpen={setOpen} />;
}

function ChatPanelInner({
  workspaceId,
  open,
  setOpen,
}: {
  workspaceId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const { state, historyLoading, send, decide, newSession } = useChat(workspaceId, open);
  const [draft, setDraft] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  // Focused when the sheet opens, so the operator can type immediately.
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Keep the newest message in view as the turn streams.
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: 'end' });
  }, [open, state.items]);

  const submit = () => {
    if (!draft.trim() || state.streaming) return;
    send(draft);
    setDraft('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Client-only exports of the transcript we already hold (no backend). Both are
  // secondary actions grouped under one menu; the trigger is disabled when empty.
  const closeExportMenu = () => setExportOpen(false);

  // Serialize to Markdown and trigger a browser download.
  const exportMarkdown = () => {
    const markdown = transcriptToMarkdown(state.items, {
      sessionId: state.sessionId,
      date: new Date().toISOString().slice(0, 10),
    });
    downloadTextFile(markdown, `chat-${state.sessionId ?? 'conversation'}.md`);
    closeExportMenu();
  };

  // Render the whole conversation to a print-styled document → browser Save-as-PDF.
  const exportSessionPdf = () => {
    printChatSessionAsPdf(state.items, {
      sessionId: state.sessionId,
      date: new Date().toISOString().slice(0, 10),
    });
    closeExportMenu();
  };

  const divider: CSSProperties = { borderTop: '1px solid var(--ehe-colors-border-default)' };

  return (
    <>
      {!open && (
        <Fab
          aria-label="Open assistant"
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          icon={<Icon as={Icons.MessageCircle} size="lg" tone="current" />}
        />
      )}

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        side="right"
        width="26rem"
        labelledBy="chat-panel-title"
        initialFocusRef={inputRef}
        data-testid="chat-panel"
      >
        {/* Header */}
        <Cluster
          as="header"
          wrap={false}
          gap="1"
          align="center"
          style={{ padding: '12px 16px', flexShrink: 0 }}
        >
          <Icon as={Icons.MessageCircle} size="sm" tone="action" />
          <Text as="h2" id="chat-panel-title" size="lg" weight="semibold" style={{ flexGrow: 1 }}>
            Assistant
          </Text>
          <div
            data-testid="chat-status"
            data-state={state.streaming ? 'streaming' : 'idle'}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}
          >
            {state.streaming && <Spinner size="xs" tone="action" label="Streaming" />}
            <Text size="xs" tone="secondary">
              {state.streaming ? 'Thinking…' : 'Ready'}
            </Text>
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label="New chat — clear conversation"
            title="New chat — clear conversation"
            data-testid="chat-new"
            disabled={state.streaming}
            onClick={newSession}
            icon={<Icon as={Icons.MessageSquarePlus} size="sm" tone="current" />}
          />
          <Menu
            open={exportOpen}
            onClose={closeExportMenu}
            label="Export conversation"
            align="end"
            trigger={
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Export conversation"
                title="Export conversation"
                data-testid="chat-export"
                aria-haspopup="menu"
                aria-expanded={exportOpen}
                disabled={state.items.length === 0}
                onClick={() => setExportOpen((o) => !o)}
                icon={<Icon as={Icons.Download} size="sm" tone="current" />}
              />
            }
          >
            <MenuItem data-testid="chat-export-markdown" onClick={exportMarkdown}>
              <Cluster gap="2" wrap={false} align="center">
                <Icon as={Icons.FileText} size="sm" tone="secondary" />
                <Text as="span" size="md">
                  Download as Markdown
                </Text>
              </Cluster>
            </MenuItem>
            <MenuItem data-testid="chat-download-pdf" onClick={exportSessionPdf}>
              <Cluster gap="2" wrap={false} align="center">
                <Icon as={Icons.FileDown} size="sm" tone="secondary" />
                <Text as="span" size="md">
                  Download as PDF
                </Text>
              </Cluster>
            </MenuItem>
          </Menu>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label="Close assistant"
            onClick={() => setOpen(false)}
            icon={<Icon as={Icons.X} size="sm" tone="current" />}
          />
        </Cluster>
        <div style={divider} />

        {/* Transcript */}
        <div style={{ flexGrow: 1, overflowY: 'auto', padding: '16px', minHeight: 0 }}>
          {historyLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <Spinner size="sm" tone="action" />
            </div>
          ) : state.items.length === 0 ? (
            <Stack gap="2" align="center" style={{ padding: '48px 0' }}>
              <Icon as={Icons.Sparkles} size="xl" tone="muted" />
              <Text size="sm" tone="secondary" align="center">
                Ask the assistant about your inbox, calendar, or CRM. Actions pause here for your
                approval.
              </Text>
            </Stack>
          ) : (
            <Stack gap="3">
              {state.items.map((item) => {
                switch (item.kind) {
                  case 'tool_call':
                    return (
                      <div key={item.id} style={{ display: 'flex' }}>
                        <ToolChip item={item} />
                      </div>
                    );
                  case 'approval':
                    return <ApprovalCard key={item.id} item={item} onDecide={decide} />;
                  default:
                    return <MessageBubble key={item.id} item={item} />;
                }
              })}
              {isThinking(state) && <ThinkingIndicator />}
              <div ref={endRef} />
            </Stack>
          )}
        </div>

        {/* Input */}
        <div style={divider} />
        <Cluster
          as="footer"
          wrap={false}
          gap="2"
          align="end"
          style={{ padding: '12px', flexShrink: 0 }}
        >
          <TextArea
            ref={inputRef}
            rows={1}
            placeholder={state.streaming ? 'Waiting for the assistant…' : 'Message the assistant'}
            value={draft}
            disabled={state.streaming}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            data-testid="chat-input"
            aria-label="Message the assistant"
            style={{ maxHeight: '8rem' }}
          />
          <IconButton
            variant="primary"
            aria-label="Send message"
            data-testid="chat-send"
            disabled={state.streaming || !draft.trim()}
            onClick={submit}
            icon={<Icon as={Icons.Send} size="sm" tone="current" />}
          />
        </Cluster>
      </Sheet>
    </>
  );
}
